/* ---------------------------------------------------------------------------
   Ground, as a heightfield.

   Elevation comes from Mapzen "terrarium" tiles — PNGs carrying a metre of
   elevation per pixel — proxied through `/api/terrain` because the bucket sends no
   CORS and a browser cannot read pixels it is not allowed to see. See that
   endpoint's header for the whole reason.

   THE ARITHMETIC IS SEPARATE FROM THE FETCHING, and deliberately: decoding a
   pixel into metres, deciding which tiles cover a patch, sampling a grid and
   turning that grid into triangles are all pure functions this project's tests can
   drive without a browser, a network or a canvas. `loadTerrain` is the only part
   that touches any of those, and it is the part that cannot be tested here.

   A NOTE ON THE VERTICAL, which matters more than any of the code below. This DEM
   and the register's own levels do not agree, and not by a little: sampled at the
   five structures, `deckRL` sits between 68 m BELOW the DEM surface and 82 m above
   it. The reasons are known and unfixable from here — SRTM smooths a gorge into
   its shoulders, so over the Kali Gandaki the DEM reads the valley walls rather
   than the river; and a Nepali bridge drawing's levels are often on a project
   benchmark rather than on a national datum. So nothing here places anything at an
   absolute level. Heights are RELATIVE to the DEM under the structure, and the
   deck goes above that by the freeboard the record states. What the 3D view shows
   is a bridge over its valley, at the right size and the right clearance; it is
   not a survey and the two datums are not reconciled.
   --------------------------------------------------------------------------- */

import { TILE, project } from './mercator.ts';

/** Deepest level the elevation dataset carries — see /api/terrain. */
export const DEM_MAX_ZOOM = 14;

/** The proxy, not the bucket. Same origin, so the pixels can be read. */
export function demTileUrl(z: number, x: number, y: number): string {
  return `/api/terrain?z=${z}&x=${x}&y=${y}`;
}

/**
 * One pixel of a terrarium tile, as metres above sea level.
 *
 * `(R × 256 + G + B ÷ 256) − 32768`, which is the format's whole definition: the
 * offset puts the Dead Sea and the Mariana Trench inside an unsigned range, and
 * the blue channel is the fractional metre.
 */
export function decodeElevation(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768;
}

/** A square of ground, in the terms everything below shares. */
export interface Patch {
  /** Centre, in degrees. */
  lat: number;
  lng: number;
  /** Side of the square, in metres. */
  span: number;
  /** Samples per side of the height grid — `samples²` points, `(samples-1)²` quads. */
  samples: number;
}

/**
 * Metres per pixel at a latitude and zoom.
 *
 * Web Mercator's scale is only true at the equator; everywhere else it is
 * stretched by 1/cos(lat), which is exactly the factor to divide back out.
 */
export function metresPerPixel(lat: number, zoom: number): number {
  return (40_075_016.686 * Math.cos((lat * Math.PI) / 180)) / (TILE * 2 ** zoom);
}

/**
 * The DEM zoom that gives a patch about one sample per grid point.
 *
 * Asking for more detail than the grid can hold is bytes fetched and thrown away;
 * asking for less makes a mesh with visible steps. Capped at the dataset's own
 * limit, because past that the tiles carry no more information.
 */
export function demZoomFor(patch: Patch): number {
  const wanted = patch.span / patch.samples; // metres per grid step
  for (let z = DEM_MAX_ZOOM; z > 0; z--) {
    if (metresPerPixel(patch.lat, z) >= wanted) return z;
  }
  return 1;
}

/** A tile the patch needs, with where it sits in world pixels. */
export interface DemTile {
  z: number;
  x: number;
  y: number;
}

/**
 * Every DEM tile a patch touches.
 *
 * Half a span each way from the centre, in world pixels at the DEM's zoom, then
 * floored to tile boundaries. One extra row and column fall out of the flooring,
 * which is wanted: a grid sampled right up to the edge of a tile needs the
 * neighbour to interpolate against.
 */
export function demTilesFor(patch: Patch, z: number): DemTile[] {
  const perPixel = metresPerPixel(patch.lat, z);
  const halfPx = patch.span / 2 / perPixel;
  const centre = project(patch.lng, patch.lat, z);
  const count = 2 ** z;

  const tiles: DemTile[] = [];
  const first = Math.floor((centre.x - halfPx) / TILE);
  const last = Math.floor((centre.x + halfPx) / TILE);
  const top = Math.floor((centre.y - halfPx) / TILE);
  const bottom = Math.floor((centre.y + halfPx) / TILE);
  for (let y = top; y <= bottom; y++) {
    if (y < 0 || y >= count) continue;
    for (let x = first; x <= last; x++) {
      tiles.push({ z, x: ((x % count) + count) % count, y });
    }
  }
  return tiles;
}

/**
 * Where a grid point sits, as a coordinate.
 *
 * The grid runs west to east and NORTH TO SOUTH — row 0 is the north edge —
 * which matches both an image's rows and a mesh's triangle winding, and saves a
 * flip in two places by costing a comment in one.
 */
export function gridPoint(patch: Patch, col: number, row: number): { lat: number; lng: number } {
  const step = patch.span / (patch.samples - 1);
  const east = col * step - patch.span / 2;
  const north = patch.span / 2 - row * step;
  const lat = patch.lat + north / 111_320;
  return { lat, lng: patch.lng + east / (111_320 * Math.cos((patch.lat * Math.PI) / 180)) };
}

/** What a decoded DEM looks like to the sampler: elevation at a coordinate. */
export type Sampler = (lat: number, lng: number) => number;

/**
 * Sample a patch into a height grid, in metres relative to its centre.
 *
 * Relative, and that is the load-bearing decision of this whole module — see the
 * note at the top of the file. The centre's own elevation is subtracted from
 * everything, so the structure sits at zero and the valley falls away from it. The
 * absolute value is returned separately for anyone who wants to say what it was.
 */
export function sampleHeights(
  patch: Patch,
  sample: Sampler,
): { heights: Float32Array; centre: number; min: number; max: number } {
  const centre = sample(patch.lat, patch.lng);
  const heights = new Float32Array(patch.samples * patch.samples);
  let min = Infinity;
  let max = -Infinity;

  for (let row = 0; row < patch.samples; row++) {
    for (let col = 0; col < patch.samples; col++) {
      const at = gridPoint(patch, col, row);
      const h = sample(at.lat, at.lng) - centre;
      heights[row * patch.samples + col] = h;
      min = Math.min(min, h);
      max = Math.max(max, h);
    }
  }
  return { heights, centre, min, max };
}

/**
 * The heightfield as triangles, in a local metric frame.
 *
 * X is east, Z is SOUTH and Y is up — three.js's own right-handed frame with Y
 * up, which is also what the modeller uses, so a bridge and a patch of ground
 * can share one scene without a conversion between them.
 *
 * Returned as the three typed arrays a `BufferGeometry` wants, and nothing more:
 * this module does not import three, so the geometry can be built and checked
 * without a WebGL context anywhere near it.
 */
export function terrainGeometry(
  patch: Patch,
  heights: Float32Array,
): { positions: Float32Array; uvs: Float32Array; indices: Uint32Array } {
  const n = patch.samples;
  const step = patch.span / (n - 1);
  const positions = new Float32Array(n * n * 3);
  const uvs = new Float32Array(n * n * 2);

  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const i = row * n + col;
      positions[i * 3] = col * step - patch.span / 2;
      positions[i * 3 + 1] = heights[i] ?? 0;
      positions[i * 3 + 2] = row * step - patch.span / 2;
      // Rows run north to south and so does a texture's V, so this is a direct
      // mapping — the draped map tile lands the right way up with no flip.
      uvs[i * 2] = col / (n - 1);
      uvs[i * 2 + 1] = 1 - row / (n - 1);
    }
  }

  const indices = new Uint32Array((n - 1) * (n - 1) * 6);
  let k = 0;
  for (let row = 0; row < n - 1; row++) {
    for (let col = 0; col < n - 1; col++) {
      const a = row * n + col;
      const b = a + 1;
      const c = a + n;
      const d = c + 1;
      // Counter-clockwise seen from above, so the surface faces up.
      indices[k++] = a;
      indices[k++] = c;
      indices[k++] = b;
      indices[k++] = b;
      indices[k++] = c;
      indices[k++] = d;
    }
  }

  return { positions, uvs, indices };
}

/* --------------------------- the part that fetches --------------------------- */

/** A decoded DEM tile: one elevation per pixel, row-major from its north-west corner. */
interface LoadedTile extends DemTile {
  elevations: Float32Array;
}

/**
 * Read one proxied DEM tile into elevations.
 *
 * Through an `<img>` and a canvas rather than `fetch` + a PNG decoder, because the
 * browser already has a PNG decoder and it is not in our bundle. Same-origin, so
 * the canvas is untainted and `getImageData` is allowed — which is the entire
 * reason /api/terrain exists.
 */
async function loadTile(tile: DemTile): Promise<LoadedTile | null> {
  const image = new Image();
  image.src = demTileUrl(tile.z, tile.x, tile.y);
  try {
    await image.decode();
  } catch {
    // Outside the dataset, or the proxy said no. Treated as "no elevation known"
    // by the caller, which flattens that tile rather than failing the view.
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = TILE;
  canvas.height = TILE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(image, 0, 0);
  const { data } = ctx.getImageData(0, 0, TILE, TILE);

  const elevations = new Float32Array(TILE * TILE);
  for (let i = 0; i < elevations.length; i++) {
    elevations[i] = decodeElevation(data[i * 4]!, data[i * 4 + 1]!, data[i * 4 + 2]!);
  }
  return { ...tile, elevations };
}

/** A patch of ground, ready to be turned into a mesh. */
export interface Terrain {
  patch: Patch;
  /** Relative heights, metres, `samples²` of them. */
  heights: Float32Array;
  /** The DEM's absolute elevation at the patch centre — what the heights are relative TO. */
  centreElevation: number;
  /** Relief within the patch, metres, for framing a camera. */
  min: number;
  max: number;
  /** DEM zoom actually used, and how many tiles it took. */
  zoom: number;
  tiles: number;
}

/**
 * Fetch, decode and sample the ground under a patch.
 *
 * Bilinear between the four surrounding postings rather than nearest — a 30 m DEM
 * sampled nearest onto a metre-scale grid produces visible terraces, and the
 * interpolation costs four multiplies per grid point.
 *
 * A patch with no tiles at all comes back flat rather than as an error: the 3D
 * view then shows a bridge on level ground and says the ground is unknown, which
 * is a truthful picture and better than nothing on screen.
 */
export async function loadTerrain(patch: Patch): Promise<Terrain> {
  const zoom = demZoomFor(patch);
  const wanted = demTilesFor(patch, zoom);
  const loaded = (await Promise.all(wanted.map(loadTile))).filter(
    (t): t is LoadedTile => t !== null,
  );

  const byKey = new Map(loaded.map((t) => [`${t.x}/${t.y}`, t]));
  const size = TILE * 2 ** zoom;

  const sample: Sampler = (lat, lng) => {
    const p = project(lng, lat, zoom);
    // Wrap x so a patch on the antimeridian still finds its tiles.
    const px = ((p.x % size) + size) % size;
    const py = Math.min(size - 1, Math.max(0, p.y));

    const at = (ix: number, iy: number): number => {
      const tile = byKey.get(`${Math.floor(ix / TILE)}/${Math.floor(iy / TILE)}`);
      if (!tile) return 0;
      const ox = ((ix % TILE) + TILE) % TILE;
      const oy = ((iy % TILE) + TILE) % TILE;
      return tile.elevations[oy * TILE + ox] ?? 0;
    };

    const x0 = Math.floor(px);
    const y0 = Math.floor(py);
    const fx = px - x0;
    const fy = py - y0;
    const h00 = at(x0, y0);
    const h10 = at(x0 + 1, y0);
    const h01 = at(x0, y0 + 1);
    const h11 = at(x0 + 1, y0 + 1);
    return (
      h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) + h01 * (1 - fx) * fy + h11 * fx * fy
    );
  };

  const { heights, centre, min, max } = sampleHeights(patch, sample);
  return { patch, heights, centreElevation: centre, min, max, zoom, tiles: loaded.length };
}

/**
 * The map tiles that cover a patch, and where they sit within it.
 *
 * The 3D view drapes the SAME raster tiles the 2D map draws, so the two views
 * agree about what the ground looks like — and so that switching between them is
 * a change of angle rather than of map. Offsets are fractions of the patch, 0..1
 * from its north-west corner, which is what a texture atlas wants.
 */
export function drapeTilesFor(
  patch: Patch,
  zoom: number,
): { z: number; x: number; y: number; u: number; v: number; size: number }[] {
  const perPixel = metresPerPixel(patch.lat, zoom);
  const halfPx = patch.span / 2 / perPixel;
  const centre = project(patch.lng, patch.lat, zoom);
  const originX = centre.x - halfPx;
  const originY = centre.y - halfPx;
  const spanPx = halfPx * 2;
  const count = 2 ** zoom;

  const out: { z: number; x: number; y: number; u: number; v: number; size: number }[] = [];
  for (let ty = Math.floor(originY / TILE); ty <= Math.floor((originY + spanPx) / TILE); ty++) {
    if (ty < 0 || ty >= count) continue;
    for (let tx = Math.floor(originX / TILE); tx <= Math.floor((originX + spanPx) / TILE); tx++) {
      out.push({
        z: zoom,
        x: ((tx % count) + count) % count,
        y: ty,
        u: (tx * TILE - originX) / spanPx,
        v: (ty * TILE - originY) / spanPx,
        size: TILE / spanPx,
      });
    }
  }
  return out;
}

/** Where a coordinate lands in a patch's local metric frame — X east, Z south. */
export function toLocal(patch: Patch, lat: number, lng: number): { x: number; z: number } {
  return {
    x: (lng - patch.lng) * 111_320 * Math.cos((patch.lat * Math.PI) / 180),
    z: -(lat - patch.lat) * 111_320,
  };
}
